import React, {useContext} from "react"
import tracking from "@irati-tracking/wellness"
import searchNothingFound from "@irati/ui/images/search_nothing_found.svg"
import {match, P} from "ts-pattern"
import Button from "@irati/ui/src/Button"
import Spacing from "@irati/ui/src/Spacing"
import {WellnessMedia} from "@irati/types"
import authentication from "@irati/auth"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import History, {useAppRouteContext} from "@irati/utils/history"
import {getDomain} from "@irati/utils/links"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import track from "@irati-tracking/platform/platform"
import {UTMMedium, UTMSource, addUTMToLink} from "@irati-web/platform/src/utils/shared"
import {create} from "zustand"
import {Grid} from "react-bootstrap"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import styled from "styled-components"
import elevation from "@irati/ui/src/global/elevation"
import {minWidthMedia} from "@irati/ui/src/global/media"
import {useWellnessSearch} from "@irati/wellness/hooks/useWellnessSearch"
import qs from "qs"
import {withRouter} from "react-router-dom"
import {Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import DrawerContext from "@irati/ui/src/AppContainer/Context/DrawerContext"
import Markdown from "@irati/ui/src/Markdown"
import {ButtonRouterLink, RouterLink} from "@irati/ui/src/Link"
import ErrorText from "@irati/ui/src/ErrorText"
import Loading from "@irati/ui/src/Loading"
import {useOnScrollToBottom} from "@irati/ui/src/hooks/useOnScrollToBottom"
import NoExist from "@irati-web/perks/src/components/DealList/NoExist"
import {NetworkStatus} from "@irati/utils/apollo"
import {useFetchMore} from "@irati/utils/services/apollo"
import devToggles from "@irati/dev-toggles"
import MediaCard from "../components/MediaCard"
import WellnessCategoryList, {WELLNESS_MEDIA_ALL_CATEGORY} from "../components/Category/WellnessCategoryList"

const MIN_SEARCH_TERM_LENGTH = 2

const useSearchStore = create<{
  searchTerm: string
  selectedCategoryIds: string[]
  hasInvalidSearch: boolean
  withDebounce: boolean
  setSearchTerm: (searchTerm: string) => void
  setSelectedCategories: (categoryIds: string[]) => void
  clearCategories: () => void
  toggleCategory: (categoryId: string) => string[]
  setInvalidSearchError: (error: boolean) => void
  setWithDebounce: (withDebounce: boolean) => void
}>()((set, get) => ({
  searchTerm: "",
  selectedCategoryIds: [],
  hasInvalidSearch: false,
  withDebounce: true,
  setSearchTerm: (searchTerm) => set({searchTerm, hasInvalidSearch: false, withDebounce: true}),
  setSelectedCategories: (selectedCategoryIds) => {
    set({selectedCategoryIds, withDebounce: false})
  },
  clearCategories: () => set({selectedCategoryIds: []}),
  toggleCategory: (categoryId) => {
    const current = get().selectedCategoryIds

    if (categoryId === WELLNESS_MEDIA_ALL_CATEGORY.id) {
      const newSelection: string[] = []
      set({selectedCategoryIds: newSelection, withDebounce: false})
      return newSelection
    }

    const categorySet = new Set(current)
    categorySet.has(categoryId) ? categorySet.delete(categoryId) : categorySet.add(categoryId)

    const newSelection = Array.from(categorySet)
    set({selectedCategoryIds: newSelection, withDebounce: false})
    return newSelection
  },

  setInvalidSearchError: (hasInvalidSearch) => set({hasInvalidSearch}),
  setWithDebounce: (withDebounce) => set({withDebounce})
}))

export const WellnessSearchPage = withRouter(({location: {search: urlSearch}}: any) => {
  const urlSearchParam: any = qs.parse(urlSearch, {ignoreQueryPrefix: true})

  const {formatMessage} = useIntl()
  const [hasFocus, setHasFocus] = React.useState(false)

  const searchTerm = useSearchStore((state) => state.searchTerm)
  const selectedCategoryIds = useSearchStore((state) => state.selectedCategoryIds)
  const hasInvalidSearch = useSearchStore((state) => state.hasInvalidSearch)
  const withDebounce = useSearchStore((state) => state.withDebounce)
  const setInvalidSearchError = useSearchStore((state) => state.setInvalidSearchError)
  const setSearchTerm = useSearchStore((state) => state.setSearchTerm)
  const setSelectedCategories = useSearchStore((state) => state.setSelectedCategories)
  const clearCategories = useSearchStore((state) => state.clearCategories)
  const toggleCategory = useSearchStore((state) => state.toggleCategory)

  const searchInput = React.useMemo(() => {
    const input = {
      search: searchTerm,
      limit: 18,
      ...(selectedCategoryIds.length > 0 ? {categoryIds: selectedCategoryIds} : {})
    }
    return input
  }, [searchTerm, selectedCategoryIds])

  const wellnessSearch = useWellnessSearch(searchInput, {
    showAllResults: !searchTerm && selectedCategoryIds.length === 0,
    debounceTime: withDebounce ? undefined : 0
  })
  const [fetchMore, fetchMoreState] = useFetchMore(wellnessSearch)

  useOnScrollToBottom({onNewPage: fetchMore})

  React.useEffect(() => {
    tracking.wellnessViewSearchPage()
  }, [])

  // TODO: the syncing in here is quite grim and a bit slow, but it works.
  // Sync URL search parameter to store
  React.useEffect(() => {
    const urlQuery = urlSearchParam.q || ""
    const urlCategoryIds = urlSearchParam.categoryId
      ? Array.isArray(urlSearchParam.categoryId)
        ? urlSearchParam.categoryId
        : [urlSearchParam.categoryId]
      : []

    if (urlQuery !== searchTerm) {
      setSearchTerm(urlQuery)
    }
    if (JSON.stringify(urlCategoryIds) !== JSON.stringify(selectedCategoryIds)) {
      setSelectedCategories(urlCategoryIds)
    }
  }, [urlSearchParam.q, urlSearchParam.categoryId, searchTerm, selectedCategoryIds, setSearchTerm, setSelectedCategories])

  const handleInputChange = (newValue: string) => {
    setInvalidSearchError(false)
    setSearchTerm(newValue)
    clearCategories()

    updateURL(newValue, [])
  }

  const handleCategoryToggle = (categoryId: string) => {
    const newSelection = toggleCategory(categoryId)
    updateURL(searchTerm, newSelection)
  }

  const updateURL = (search: string, categoryIds: string[]) => {
    const params = new URLSearchParams()
    if (search) params.set("q", search)

    const realCategoryIds = categoryIds.filter((id) => id !== WELLNESS_MEDIA_ALL_CATEGORY.id)
    if (realCategoryIds.length > 0) {
      realCategoryIds.forEach((id) => params.append("categoryId", id))
    }

    const queryString = params.toString()
    const newSearch = queryString ? `?${queryString}` : ""
    History.replace(`/wellness/search${newSearch}`)
  }

  return (
    <Grid>
      <Flex direction="column" gap={4} marginTop={4}>
        <div>
          <Icon icon={IconTypes.arrowLeft} marginRight={2} />
          <ButtonRouterLink to="/">
            <FormattedMessage id="wellness.search.back" />
          </ButtonRouterLink>
        </div>
        <InputWrapper
          hasFocus={hasFocus}
          onSubmit={(e) => {
            e.preventDefault()
            if (searchTerm.length < MIN_SEARCH_TERM_LENGTH) {
              setInvalidSearchError(true)
            }
          }}>
          <SearchIcon className="pb-search_small-01" />
          <Input
            type="text"
            onChange={(e) => handleInputChange(e.target.value)}
            value={searchTerm}
            name="search"
            placeholder={formatMessage({id: "wellness.search.placeholder"})}
            onFocus={() => setHasFocus(true)}
            onBlur={() => setHasFocus(false)}
          />
        </InputWrapper>

        {hasInvalidSearch ? (
          <ErrorText>
            <FormattedMessage id="wellness.search.invalidSearchError" values={{searchTerm, minLength: MIN_SEARCH_TERM_LENGTH}} />
          </ErrorText>
        ) : null}

        {devToggles.showNewWellness() && (
          <WellnessCategoryList showLink selectedCategoryIds={selectedCategoryIds} onCategoryToggle={handleCategoryToggle} />
        )}

        {match(wellnessSearch)
          .with({called: false}, () => null)
          .with({networkStatus: P.union(NetworkStatus.loading, NetworkStatus.setVariables)}, () => <Loading isLoading />)
          .with({errorMsg: P.string}, ({errorMsg}) => <ErrorText>{errorMsg}</ErrorText>)
          .with({data: {results: []}}, () => <EmptyState />)
          .with({data: {results: P.array(P._)}}, (apollo) => {
            const results = padArrayToMinLength(apollo.data.results, 3, null)

            return (
              <>
                <Body>
                  <FormattedMessage
                    id="wellness.search.resultsCount"
                    values={{shownResults: apollo.data.offset, allResults: apollo.data.total}}
                  />
                </Body>

                <Results results={results} />

                {match(fetchMoreState)
                  .with({loading: true}, () => <Loading isLoading inline />)
                  .with({error: P.string}, ({error: errorMsg}) => (
                    <Flex direction="column" gap={2} alignItems="center">
                      <ErrorText>{errorMsg}</ErrorText>
                      {/* TODO: <Button large primary onClick={() => wellnessSearch.refetch()}>
                          try again
                        </Button> */}
                    </Flex>
                  ))
                  .with({hasMore: true}, () => (
                    <Flex justifyContent="center">
                      <Button large primary onClick={() => fetchMore()} disabled={!fetchMoreState.hasMore}>
                        show more
                      </Button>
                    </Flex>
                  ))
                  .otherwise(() => (
                    <NoExist />
                  ))}
              </>
            )
          })
          .exhaustive(console.error)}
      </Flex>
    </Grid>
  )
})

// TODO: do better memo so the cards dont rerender
const Results = React.memo(ResultList)

function ResultList({results}: {results: (WellnessMedia | null)[]}) {
  const {setDrawerOpen} = useContext(DrawerContext)
  const {formatMessage} = useIntl()

  return (
    <GridContainer>
      {results?.map((result, index) =>
        result === null ? (
          <Spacing key={`empty-${index}`} />
        ) : (
          <GridItem key={result.id}>
            <MediaCard key={result.id} media={result} onShowAlert={() => handleShareLink(result, setDrawerOpen, formatMessage)} />
          </GridItem>
        )
      )}
    </GridContainer>
  )
}

const SearchIcon = styled.i`
  font-size: 20px;
`

interface InputWrapperProps {
  hasFocus: boolean
}

// much of this is copied from deals search but time is short
const InputWrapper = styled.form<InputWrapperProps>`
  height: 40px;
  background: ${({theme}) => theme.colors.greys.white};
  display: flex;
  border: 1px solid ${({theme}) => theme.colors.greys.light};
  border-radius: 5px;
  transition:
    transform 0.12s,
    box-shadow 0.12s;
  align-items: center;

  ${minWidthMedia.tablet`
    height: 60px;
  `}

  ${(props) =>
    props.hasFocus &&
    `
    ${elevation.twoPxElevation};
    transform: translateY(-4px);
  `}

  ${SearchIcon} {
    margin: 0 15px;
  }
`

const Input = styled.input`
  margin: 0 !important;
  border: none !important;
  outline: none !important;
  padding: 5px 0 !important;
  width: 100%;

  ${minWidthMedia.tablet`
      padding: 10px 0 !important;
    `}
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 10px;
`

const GridItem = styled.div`
  width: 100%;
`

function padArrayToMinLength<T>(array: T[], minLength: number, padValue: T | null = null): (T | null)[] {
  if (array.length >= minLength) return array
  const paddingNeeded = minLength - array.length
  return [...array, ...Array(paddingNeeded).fill(padValue)]
}

function EmptyState() {
  const {app, apps} = useAppRouteContext()
  return (
    <Flex gap={3}>
      <img src={searchNothingFound} alt="No results found" />

      <Flex direction="column">
        <Markdown
          source="wellness.search.nothingFound"
          values={{
            link: (
              <RouterLink to="/" cta large app={apps[app]}>
                <FormattedMessage id="wellness.search.nothingFoundLinkName" />
              </RouterLink>
            )
          }}
        />
      </Flex>
    </Flex>
  )
}

// TODO: copied from home, needs abstracting
function handleShareLink(media: WellnessMedia, setDrawerOpen: (drawerData: any) => void, formatMessage: any) {
  const drawerData = {
    url: addUTMToLink(
      `${getDomain(authentication.business().subdomain)}/wellness/details/${media.uuid}`,
      UTMSource.WELLNESS_CARD,
      UTMMedium.SHARE_LINK
    ),
    title: media.title,
    alertText: "platform.share.alertText",
    source: "wellness",
    type: "card",
    entity_id: media.uuid
  }

  if (navigator?.canShare?.(drawerData)) {
    setDrawerOpen(drawerData)
  } else {
    navigator.clipboard.writeText(drawerData.url).then(() => {
      track.share({source: "wellness", type: "card", entity_id: media.uuid})
      triggerSuccessToast(formatMessage({id: "wellness.copyVideoLink.title"}))
    })
  }
}
